import React, { Component, ErrorInfo, ReactNode } from 'react';
import { BuiltBrandLockup } from './ui/BuiltBrandLockup';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Catches render crashes only — not API/validation errors.
 * Mounted at app root; keep recovery calm and brand-aligned.
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });

    if (process.env.NODE_ENV === 'production') {
      console.error('Production error:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleHome = () => {
    window.location.href = '/';
  };

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center px-4">
          <div className="mb-8">
            <BuiltBrandLockup heightClass="h-9" />
          </div>
          <div className="card-elevated max-w-md w-full p-6 sm:p-8 text-center">
            <h1 className="text-xl font-semibold text-white tracking-tight mb-2">
              Something went wrong
            </h1>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              Your garage and builds are safe. Refresh the page, or go home and try again.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="text-left mb-6 bg-[#0a0a0a] rounded-xl border border-white/[0.06] p-4">
                <summary className="text-orange-300 cursor-pointer text-sm mb-2">
                  Error details (development)
                </summary>
                <pre className="text-xs text-gray-500 overflow-auto max-h-40 whitespace-pre-wrap">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <button type="button" onClick={this.handleReload} className="btn-primary flex-1 min-h-[44px]">
                Refresh page
              </button>
              <button type="button" onClick={this.handleReset} className="btn-secondary flex-1 min-h-[44px]">
                Try again
              </button>
              <button type="button" onClick={this.handleHome} className="btn-ghost flex-1 min-h-[44px]">
                Go home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
